import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, {FC, useMemo} from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDialogModal, useDrawerModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, setValues} from "@/helper/method";
import {useCreateWorktime, useQueryWorktime, WorktimeVO} from "@/service/worktime.s";
import {AccessTimeOutlined} from '@mui/icons-material';
import {SearchData} from "@/widget/SearchData/SearchData";
import {SearchPlanVO} from "@/service/plan.s";
import isEmpty from "lodash/isEmpty";
import {TaskPO} from "@/model/mission";
import {ShowChartOutlined} from '@mui/icons-material';
import {SearchTaskVO, useQueryTask} from "@/service/task.s";
import {isNumber} from "lodash";
import {WriteReportForm} from "@/pages/Report/WriteReport";

const validationSchema = z.object({
    planId: z.number({message: "请选择关联目标"}),
    planName: z.ostring().optional(),
    status: z.ostring().default(""),
    statusId: z.number({message: "请选任务状态"}),
    planStageId: z.number({message: "请选择所属目标阶段"}),
    planStage: z.ostring().default(""),
    duration: z.number({message: "请选择预计工时"}),
    priority: z.ostring().optional().default(""),
    taskName: z.string().optional(),
    taskId: z.number({message: "请选择任务名称"}).optional(),
    hours: z.number({message: "请填写工作时长"}).max(1000, "工作时长不能超过1000"),
    categoryId: z.number({message: "请选择工时类别"}),
    category: z.ostring().default(""),
    description: z.string().optional(),
    priorityId: z.number({message: "请选择优先级"}),
});

export type WriteWorktimeForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export interface WriteWorktimeProps {
    fromTask?: boolean
}

export const WriteWorktime: FC<WriteWorktimeProps> = ({fromTask = false}) => {
    const allDict = useAllDict()
    const {taskStatus, planStage, taskPriority, worktimeType} = allDict
    const {setToast} = useGlobalActions()
    const {mutateAsync: createWorktimeMutate, isPending: createWorktimePending} = useCreateWorktime()
    const {onSearch, id: drawerId, write} = useDrawerModal()
    const {id: dialogId} = useDialogModal()
    const {setDrawerModal, setDialogModal} = useGlobalActions()
    const {data: taskData} = useQueryTask({id: drawerId! || dialogId!}, isNumber(drawerId) && fromTask)
    const {data: worktimeData} = useQueryWorktime({id: drawerId! || dialogId!}, !isEmpty(drawerId) || !isEmpty(dialogId))
    const task = taskData?.data || {} as TaskPO
    const worktime = worktimeData?.data || {} as WorktimeVO
    const {disable, values} = useMemo(() => {
        if (write == 'edit') {
            return {disable: false, values: worktime}
        } else if (fromTask) {
            return {
                disable: true, values: {
                    taskId: task.id,
                    taskName: task.taskName,
                    planName: task.planName,
                    planId: task.planId,
                    status: task.status,
                    statusId: task.statusId,
                    planStage: task.planStage,
                    planStageId: task.planStageId,
                    duration: task.duration,
                    priority: task.priority,
                    priorityId: task.priorityId,
                }
            }
        }
        return {disable: false, values: {}}
    }, [write, fromTask, task, worktime])

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteWorktimeForm>({
        resolver: zodResolver(validationSchema),
        values: values as WriteWorktimeForm,
        defaultValues,
    });

    const onSubmit = async (values: WriteWorktimeForm) => {
        const result = await createWorktimeMutate({...values, taskId: dialogId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setDialogModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const planId: number = useWatch({
        control,
        name: "planId",
        defaultValue: 0
    })

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <SearchData<WriteWorktimeForm, SearchPlanVO> errMsg={errors?.planId?.message} readOnly={disable} setValue={setValue} tag={5} attr={"planName"} attrId={"planId"}
                                                                 label={"关联目标"}
                                                                 control={control}/>
                </Grid>
                <Grid size={6}>
                    <SearchData<WriteReportForm, SearchTaskVO> errMsg={errors?.taskId?.message} query={{planId}} readOnly={disable || !isNumber(planId)} setValue={setValue} tag={6}
                                                               attr={"taskName"} attrId={"taskId"}
                                                               label={"任务名称"}
                                                               onSelect={(item: SearchTaskVO) => {
                                                                   setValues<Pick<WriteReportForm, "status" | "statusId" | "duration" | "planStageId" | "planStage" | "priority" | "priorityId">>({
                                                                       planStageId: item?.planStageId,
                                                                       planStage: item?.planStage,
                                                                       duration: item?.duration,
                                                                       priority: item?.priority,
                                                                       priorityId: item?.priorityId,
                                                                       status: item?.status,
                                                                       statusId: item?.statusId,
                                                                   }, setValue as any)
                                                               }}
                                                               control={control}/></Grid>


                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>任务状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={taskStatus}
                                value={value as any}
                                readOnly={true}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("statusId", option!.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.statusId}
                                               helperText={errors?.statusId?.message}
                                               variant="outlined"
                                               placeholder={"选择任务状态"} {...params} />
                                )}
                            />
                        }} name={"status"}/>
                    </FormControl>
                </Grid>

                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>优先级</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={taskPriority}
                            value={value as any}
                            readOnly={true}
                            onChange={(_, option: DictPO | null) => {
                                setValue("priorityId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.priorityId}
                                           helperText={errors?.priorityId?.message}
                                           variant="outlined"
                                           placeholder={"选择优先级"} {...params} />
                            )}
                        />} name={"priority"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel htmlFor="planStage">所属目标阶段</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            id="planStage"
                            options={planStage}
                            readOnly={true}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("planStageId", option!.value)
                                onChange(option?.label)
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField slotProps={{
                                    input: {
                                        readOnly: true,
                                    }
                                }} error={!!errors?.planStageId}
                                           helperText={errors?.planStageId?.message}
                                           variant="outlined"
                                           placeholder={"选择目标阶段"} {...params} />
                            )}
                        />} name={"planStage"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="duration">预计工时</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.duration}
                            helperText={errors?.duration?.message}
                            id="duration"
                            value={value}
                            onChange={event => event.target.value && onChange(parseInt(event.target.value))}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    inputProps: {
                                        min: 0,
                                        max: 1000,
                                        maxLength: 3, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <AccessTimeOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写预计工时"
                            variant="outlined"
                        />} name={'duration'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="hours">工作时长</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.hours}
                            helperText={errors?.hours?.message}
                            id="hours"
                            type={'number'}
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value))}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 100,
                                        maxLength: 3,
                                        inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <ShowChartOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写工作时长"
                            variant="outlined"
                        />} name={'hours'}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel htmlFor="category">工时类别</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            id="category"
                            options={worktimeType}
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("categoryId", option?.value || 0)
                                onChange(option?.label || "")
                            }}
                            fullWidth
                            renderInput={(params) => (
                                <TextField error={!!errors?.categoryId}
                                           helperText={errors?.categoryId?.message}
                                           variant="outlined"
                                           placeholder={"选择工时类别"} {...params} />
                            )}
                        />} name={"category"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="description">工作描述</FormLabel>
                        <TextField
                            id="description"
                            multiline
                            rows={3}
                            {...register("description")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                },
                            }}
                            placeholder="工作描述"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createWorktimePending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
